import getArchiveAwareDisplayName from 'archive/getArchiveAwareDisplayName';
import isArchived from 'archive/isArchived';
import {RegistryFilterOption} from 'constants/RegistryConstants';
import {getSearchableSchemaInterfaceOptionDatas} from 'schemas/schema-interface/InterfaceSearchUtil';
import {SchemaInterface} from 'schemas/schema-interface/SchemaInterfaceConstants';
import {isSchemaTypeFilterOption, Schema, SchemaTypeFilterOption} from 'schemas/SchemaConstants';
import {getSchemaOptionDataMetadata} from 'schemas/SchemaOwnerUtil';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {OptionValue} from 'ui-library/menus/MenuConstants';
import {SearchableMenuArchivableOptionDatas} from 'ui-library/menus/SearchableMenu';
import APIUtil from 'util/APIUtil';
import pickDefined from 'util/pickDefined';
import {Optional} from 'util/Types';

import {SchemaSelectorSchema} from './SchemaSelector';

export interface Section {
  title?: string;
  value?: OptionValue;
  containsSchema: (schema: Schema | RegistryFilterOption) => boolean;
}

interface SchemaMenuOptions<T extends Schema | RegistryFilterOption | SchemaTypeFilterOption> {
  shouldShowNoSchemaOption?: boolean;
  allSchemasValue?: OptionValue;
  unstarrable?: boolean;
  // Allows caller to group unstarred schemas. Specify title, value, and how to group schema.
  sections?: Array<Section>;
  transformSchema?: (schema: Schema | RegistryFilterOption) => OptionValue;
  transformInterface?: (schemaInterface: SchemaSelectorSchema) => OptionValue;
  isSchemaDisabledFn?: (value: Schema | RegistryFilterOption) => boolean;
  includeArchivedSuffix?: boolean;

  // A message to disable when a menu option is disabled
  disabledMessage?: string;
  /**
   * If two schemas share the same name, the owner will be rendered as a subtitle to disambiguate.
   */
  getSchemaOwner?: (value: T) => string | undefined;
  /** Icon to include to the left of each schema option */
  optionIcon?: BenchlingIconName;
}

/**
 * Returns the SearchableMenu JSON options
 * @param allSchemasValue If present, then an 'All' option is displayed with the value set to this value
 */
export function getSchemaSearchableMenuOptionDatas<
  T extends SchemaSelectorSchema | Schema | RegistryFilterOption | SchemaTypeFilterOption,
>(
  schemasAndFilterOptions: Optional<Array<T>>,
  starredSchemaIds: Optional<Array<string>>,
  options: SchemaMenuOptions<T>
): SearchableMenuArchivableOptionDatas {
  const {
    allSchemasValue,
    shouldShowNoSchemaOption,
    unstarrable,
    sections,
    transformSchema,
    transformInterface,
    isSchemaDisabledFn,
    disabledMessage,
    includeArchivedSuffix,
    optionIcon,
  }: SchemaMenuOptions<T> = {
    shouldShowNoSchemaOption: false,
    allSchemasValue: null,
    unstarrable: false,
    includeArchivedSuffix: true,
    sections: [
      {
        title: 'Schemas',
        containsSchema: () => true,
      },
    ],
    transformSchema: (schema: Schema | RegistryFilterOption) => schema,
    transformInterface: (schemaInterface: SchemaInterface) => schemaInterface,
    ...pickDefined(
      options,
      'shouldShowNoSchemaOption',
      'allSchemasValue',
      'unstarrable',
      'sections',
      'transformSchema',
      'transformInterface',
      'disabledMessage',
      'isSchemaDisabledFn',
      'includeArchivedSuffix',
      'optionIcon'
    ),
  };
  const optionDatas: SearchableMenuArchivableOptionDatas = [];
  if (!schemasAndFilterOptions || !starredSchemaIds) {
    return optionDatas;
  }

  const getMetadata = getSchemaOptionDataMetadata(schemasAndFilterOptions, (schema) =>
    options.getSchemaOwner?.(schema)
  );

  if (
    schemasAndFilterOptions.length === 0 &&
    // Render sections if any are selectable
    !sections.some((section) => section.hasOwnProperty('value'))
  ) {
    optionDatas.push({name: 'No schemas found'});
  } else {
    if (allSchemasValue) {
      optionDatas.push({name: 'All', value: allSchemasValue, extra: {unstarrable: true}, icon: optionIcon});
    }
    if (shouldShowNoSchemaOption) {
      optionDatas.push({name: 'No schema', value: {id: null}, icon: optionIcon});
    }
    const starredIds = new Set(starredSchemaIds);
    const starredSchemas = schemasAndFilterOptions.filter((schema) => starredIds.has(schema.id));
    const hasStarred = starredSchemas.length > 0;
    if (hasStarred) {
      optionDatas.push({name: 'Starred', isHeader: true, className: 'schema-section'});
      for (const schema of starredSchemas) {
        const disabled = isSchemaDisabledFn != null ? isSchemaDisabledFn(schema) : false;
        optionDatas.push({
          ...(disabledMessage != null ? {disabledMessage} : {}),
          name: includeArchivedSuffix ? getArchiveAwareDisplayName(schema) : schema.name,
          metadata: getMetadata(schema),
          value: transformSchema(schema),
          disabled,
          isArchived: isArchived(schema),
          icon: optionIcon,
        });
      }
    }
    // TODO(BNCH-87176): This forced schema interface section feels very hacky and should be refactored such
    // that the caller needs to determine an explicit section.
    const schemaInterfaces = schemasAndFilterOptions.filter((schema) => APIUtil.isSchemaInterface(schema.id));
    const nonInterfaceSchemasAndFilterOptions = schemasAndFilterOptions.filter(
      (schema) => !APIUtil.isSchemaInterface(schema.id)
    );
    if (schemaInterfaces && schemaInterfaces.length > 0) {
      getSearchableSchemaInterfaceOptionDatas(schemaInterfaces, transformInterface, {optionIcon}).forEach(
        (section) => optionDatas.push(section)
      );
    }
    sections.forEach((section) => {
      // If the section should be selectable, show it as an option.
      if (section.value && section.title) {
        optionDatas.push({
          name: section.title,
          className: 'schema-section',
          value: section.value,
          extra: {unstarrable: true},
        });
      } else if (section.title) {
        optionDatas.push({
          name: section.title,
          isHeader: true,
          className: 'schema-section',
        });
      }
      // If the section has no header, then we need to add the divider styling to the first option in the section
      let needsSectionDivider = section.title == null;
      const sectionSchemas =
        nonInterfaceSchemasAndFilterOptions?.filter((schema) => section.containsSchema(schema)) || [];
      for (const schema of sectionSchemas) {
        if (starredIds.has(schema.id)) {
          continue;
        }
        const disabled = isSchemaDisabledFn != null ? isSchemaDisabledFn(schema) : false;
        const optionIsUnstarrable = isSchemaTypeFilterOption(schema) || unstarrable;
        optionDatas.push({
          ...(disabledMessage != null ? {disabledMessage} : {}),
          className: needsSectionDivider ? 'schema-section-divider schema-option' : 'schema-option',
          name: includeArchivedSuffix ? getArchiveAwareDisplayName(schema) : schema.name,
          value: transformSchema(schema),
          metadata: getMetadata(schema),
          extra: {unstarrable: optionIsUnstarrable},
          disabled,
          isArchived: isArchived(schema),
          icon: optionIcon,
        });
        needsSectionDivider = false;
      }
    });
  }
  return optionDatas;
}
